<template>
  <v-row>
    <v-col cols="12">
      <v-form :submit="submitUpdate">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              label="Nome"
              v-model="value.name"
              :rules="rules.required"
              density="compact"
              :disabled="requestingApi"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              label="Preço base"
              v-model="value.base_price"
              type="number"
              min="0"
              step="0.01"
              :rules="rules.requiredPrice"
              density="compact"
              :disabled="requestingApi"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              label="Categoria"
              v-model="value.category_id"
              :items="categoryItems"
              item-title="title"
              item-value="value"
              clearable
              density="compact"
              :disabled="requestingApi"
              @update:model-value="onCategoryChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="SKU"
              v-model="value.sku"
              clearable
              density="compact"
              :disabled="requestingApi"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              label="Unidade"
              v-model="value.unit"
              density="compact"
              :disabled="requestingApi"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              label="Ordem PDV"
              v-model="value.pdv_sort"
              type="number"
              density="compact"
              :disabled="requestingApi"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-textarea
              label="Descrição"
              v-model="value.description"
              rows="2"
              auto-grow
              density="compact"
              :disabled="requestingApi"
            ></v-textarea>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              label="Tipo de produto"
              v-model="value.kind"
              :items="kindItems"
              item-title="title"
              item-value="value"
              density="compact"
              :disabled="requestingApi"
              hide-details="auto"
              @update:model-value="onKindChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              color="primary"
              label="Controla estoque"
              v-model="value.tracks_stock"
              :disabled="requestingApi || value.kind === 'ticket'"
              hide-details
            ></v-switch>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              color="green"
              :label="value.active ? 'Ativo' : 'Inativo'"
              v-model="value.active"
              :disabled="requestingApi"
              hide-details
            ></v-switch>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              color="blue"
              label="Visível no PDV"
              v-model="value.pdv_visible"
              :disabled="requestingApi"
              hide-details
            ></v-switch>
          </v-col>

          <template v-if="value.kind === 'ticket'">
            <v-col cols="12">
              <div class="text-subtitle-2">Ingresso (ticket)</div>
              <div class="text-caption text-medium-emphasis mb-1">
                Capacidade = estoque do grupo + alocação na loja. Um código por unidade vendida
                (após pagamento). QR = código do ingresso.
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                label="Máx. check-ins"
                v-model.number="ticketMeta.max_scans"
                type="number"
                min="1"
                step="1"
                density="compact"
                :disabled="requestingApi"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                label="Válido de"
                v-model="ticketMeta.valid_from"
                type="date"
                density="compact"
                clearable
                :disabled="requestingApi"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                label="Válido até"
                v-model="ticketMeta.valid_until"
                type="date"
                density="compact"
                clearable
                :disabled="requestingApi"
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </template>

          <!-- Attribute fields (role=attribute) — product-level metadata -->
          <template v-if="attributeFields.length">
            <v-col cols="12">
              <div class="text-subtitle-2">Atributos da categoria</div>
              <div class="text-caption text-medium-emphasis mb-1">
                Informações do produto (não criam estoque separado).
              </div>
            </v-col>
            <v-col
              v-for="field in attributeFields"
              :key="field.id || field.key"
              cols="12"
              md="6"
            >
              <v-select
                v-if="field.type === 'select'"
                :label="fieldLabel(field)"
                v-model="attributeValues[field.key]"
                :items="selectOptions(field)"
                :rules="field.required ? rules.required : []"
                density="compact"
                clearable
                :disabled="requestingApi"
              ></v-select>
              <v-text-field
                v-else-if="field.type === 'number'"
                :label="fieldLabel(field)"
                v-model="attributeValues[field.key]"
                type="number"
                :rules="field.required ? rules.required : []"
                density="compact"
                clearable
                :disabled="requestingApi"
              ></v-text-field>
              <v-text-field
                v-else-if="field.type === 'date'"
                :label="fieldLabel(field)"
                v-model="attributeValues[field.key]"
                type="date"
                :rules="field.required ? rules.required : []"
                density="compact"
                clearable
                :disabled="requestingApi"
              ></v-text-field>
              <v-text-field
                v-else
                :label="fieldLabel(field)"
                v-model="attributeValues[field.key]"
                :rules="field.required ? rules.required : []"
                density="compact"
                clearable
                :disabled="requestingApi"
              ></v-text-field>
            </v-col>
          </template>

          <!-- Variant axes info (role=variant) — matrix generated by BE -->
          <v-col v-if="variantFields.length" cols="12">
            <v-alert type="info" variant="tonal" density="compact">
              Esta categoria gera
              <strong>{{ expectedVariantCount }}</strong>
              variação(ões) de estoque:
              <span v-for="(f, i) in variantFields" :key="f.key || i">
                {{ f.label }}{{ i < variantFields.length - 1 ? ' × ' : '' }}
              </span>
              . O estoque de cada combinação é gerido na loja.
            </v-alert>
          </v-col>

          <!-- Existing variants summary when editing -->
          <v-col v-if="existingVariants.length" cols="12">
            <div class="text-subtitle-2 mb-1">
              Variações ({{ existingVariants.length }})
            </div>
            <v-chip
              v-for="v in existingVariants.slice(0, 24)"
              :key="v.id"
              size="small"
              class="ma-1"
              :color="v.active === false ? 'default' : 'primary'"
              variant="tonal"
            >
              {{ variantLabel(v) }}
              <span v-if="v.price_override != null" class="ml-1">
                · {{ formatPrice(v.price_override) }}
              </span>
            </v-chip>
            <div v-if="existingVariants.length > 24" class="text-caption mt-1">
              +{{ existingVariants.length - 24 }} outras
            </div>
          </v-col>

          <v-col cols="12" class="text-right">
            <v-btn color="success" type="submit" :loading="requestingApi" :disabled="requestingApi">
              <v-icon class="mr-2">mdi-content-save</v-icon>
              Salvar
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>
<script>
import api from '@/services'

export default {
  props: {
    user: Object,
    modelValue: Object,
    categories: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'created', 'updated'],
  data() {
    return {
      requestingApi: false,
      attributeValues: {},
      ticketMeta: {
        max_scans: 1,
        valid_from: null,
        valid_until: null,
      },
      kindItems: [
        { title: 'Padrão', value: 'standard' },
        { title: 'Ingresso', value: 'ticket' },
      ],
      rules: {
        required: [(v) => !!v || 'Campo obrigatório'],
        requiredPrice: [
          (v) =>
            (v !== null && v !== undefined && v !== '' && !isNaN(Number(v))) ||
            'Campo obrigatório',
        ],
      },
    }
  },
  created() {
    this.ensureModel()
    this.hydrateAttributes()
    this.hydrateTicketMeta()
  },
  methods: {
    ensureModel() {
      const vm = this
      if (!vm.modelValue || (!vm.modelValue.id && vm.modelValue.name == null && !vm.modelValue.kind)) {
        vm.value = vm.generateEmpty()
      } else if (vm.modelValue && !vm.modelValue.kind) {
        vm.value = { ...vm.modelValue, kind: 'standard' }
      }
    },
    generateEmpty() {
      return {
        name: '',
        base_price: null,
        category_id: null,
        description: null,
        sku: null,
        kind: 'standard',
        tracks_stock: true,
        unit: 'un',
        active: true,
        pdv_visible: true,
        pdv_sort: 0,
        ticket_meta: null,
        attributes: [],
        variants: [],
      }
    },
    selectedCategory() {
      const id = this.value?.category_id
      if (!id) return null
      return (this.categories || []).find((c) => c.id === id) || null
    },
    fieldLabel(field) {
      return field.required ? `${field.label} *` : field.label
    },
    selectOptions(field) {
      return (field.options || []).map((o) => ({ title: o, value: o }))
    },
    dateInputFromIso(value) {
      if (!value) return null
      const s = String(value)
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
      try {
        const d = new Date(s)
        if (isNaN(d.getTime())) return null
        return d.toISOString().slice(0, 10)
      } catch {
        return null
      }
    },
    hydrateTicketMeta() {
      const meta = this.value?.ticket_meta
      const src = meta && typeof meta === 'object' ? meta : {}
      const max = Number(src.max_scans)
      this.ticketMeta = {
        max_scans: !isNaN(max) && max >= 1 ? max : 1,
        valid_from: this.dateInputFromIso(src.valid_from) || null,
        valid_until: this.dateInputFromIso(src.valid_until) || null,
      }
    },
    hydrateAttributes() {
      const map = {}
      const attrs = this.value?.attributes
      if (Array.isArray(attrs)) {
        for (const a of attrs) {
          if (a?.key != null) map[a.key] = a.value ?? null
        }
      } else if (attrs && typeof attrs === 'object') {
        Object.assign(map, attrs)
      }
      for (const f of this.attributeFields) {
        if (!(f.key in map)) map[f.key] = null
      }
      this.attributeValues = map
    },
    onKindChange(kind) {
      if (kind === 'ticket') {
        this.value = { ...this.value, kind: 'ticket', tracks_stock: true }
        if (!this.ticketMeta?.max_scans) {
          this.ticketMeta = { ...this.ticketMeta, max_scans: 1 }
        }
      }
    },
    onCategoryChange() {
      const next = {}
      for (const f of this.attributeFields) {
        next[f.key] =
          this.attributeValues?.[f.key] !== undefined
            ? this.attributeValues[f.key]
            : null
      }
      this.attributeValues = next
    },
    variantLabel(v) {
      if (v?.name_suffix) return v.name_suffix
      if (v?.is_default) return 'Padrão'
      if (Array.isArray(v?.options) && v.options.length) {
        return v.options.map((o) => o.value).join(' / ')
      }
      return `#${v?.id || '?'}`
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return ''
      const n = Number(value)
      if (isNaN(n)) return value
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    buildTicketMetaPayload() {
      const m = this.ticketMeta || {}
      const max = Number(m.max_scans)
      const payload = {
        max_scans: !isNaN(max) && max >= 1 ? Math.floor(max) : 1,
      }
      if (m.valid_from) payload.valid_from = m.valid_from
      else payload.valid_from = null
      if (m.valid_until) payload.valid_until = m.valid_until
      else payload.valid_until = null
      return payload
    },
    buildPayload() {
      const m = this.value || {}
      const kind = m.kind === 'ticket' ? 'ticket' : 'standard'
      const payload = {
        name: m.name,
        base_price: m.base_price != null && m.base_price !== '' ? Number(m.base_price) : null,
        category_id: m.category_id || null,
        description: m.description || null,
        sku: m.sku || null,
        kind,
        tracks_stock: kind === 'ticket' ? true : !!m.tracks_stock,
        unit: m.unit || 'un',
        active: m.active !== false,
        pdv_visible: m.pdv_visible !== false,
        pdv_sort: m.pdv_sort != null && m.pdv_sort !== '' ? Number(m.pdv_sort) : 0,
      }
      if (kind === 'ticket') {
        payload.ticket_meta = this.buildTicketMetaPayload()
      }
      if (m.id) {
        payload.id = m.id
      }
      const attrs = {}
      for (const f of this.attributeFields) {
        const raw = this.attributeValues?.[f.key]
        if (raw !== null && raw !== undefined && raw !== '') {
          attrs[f.key] = String(raw)
        } else if (f.required) {
          attrs[f.key] = ''
        }
      }
      if (this.attributeFields.length) {
        payload.attributes = attrs
      }
      return payload
    },
    submitUpdate() {
      const vm = this
      if (vm.requestingApi) return
      if (!vm.value?.name) {
        vm.$notify({ type: 'warn', text: 'Nome é obrigatório' })
        return
      }
      if (
        vm.value?.base_price === null ||
        vm.value?.base_price === undefined ||
        vm.value?.base_price === '' ||
        isNaN(Number(vm.value.base_price))
      ) {
        vm.$notify({ type: 'warn', text: 'Preço base é obrigatório' })
        return
      }
      if (vm.value?.kind === 'ticket') {
        const max = Number(vm.ticketMeta?.max_scans)
        if (isNaN(max) || max < 1) {
          vm.$notify({ type: 'warn', text: 'Máx. check-ins deve ser ≥ 1' })
          return
        }
        const from = vm.ticketMeta?.valid_from
        const until = vm.ticketMeta?.valid_until
        if (from && until && from > until) {
          vm.$notify({ type: 'warn', text: 'Válido de não pode ser após válido até' })
          return
        }
      }
      for (const f of vm.attributeFields) {
        if (!f.required) continue
        const v = vm.attributeValues?.[f.key]
        if (v === null || v === undefined || v === '') {
          vm.$notify({ type: 'warn', text: `Atributo obrigatório: ${f.label}` })
          return
        }
      }
      if (vm.expectedVariantCount > 200) {
        vm.$notify({
          type: 'warn',
          text: 'Categoria geraria mais de 200 variações — ajuste opções da categoria',
        })
        return
      }
      vm.requestingApi = true
      return api.product
        .updateOrCreate(vm.buildPayload())
        .then((result) => {
          if (vm.value?.id) {
            vm.$emit('updated', result)
            vm.$notify({ type: 'success', text: 'Produto atualizado com sucesso' })
          } else {
            vm.$emit('created', result)
            vm.$notify({ type: 'success', text: 'Produto criado com sucesso' })
          }
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.requestingApi = false
        })
    },
  },
  computed: {
    categoryItems() {
      return (this.categories || []).map((c) => ({
        title: c.name,
        value: c.id,
      }))
    },
    categoryFields() {
      const cat = this.selectedCategory()
      const fields = cat?.fields || []
      return fields.filter((f) => f.active !== false)
    },
    attributeFields() {
      return this.categoryFields.filter((f) => f.role !== 'variant')
    },
    variantFields() {
      return this.categoryFields.filter((f) => f.role === 'variant')
    },
    expectedVariantCount() {
      const axes = this.variantFields
      if (!axes.length) return 0
      return axes.reduce((acc, f) => {
        const n = Array.isArray(f.options) ? f.options.length : 0
        if (n <= 0) return 0
        return acc * n
      }, 1)
    },
    existingVariants() {
      const list = this.value?.variants
      return Array.isArray(list) ? list : []
    },
    value: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
  },
  watch: {
    modelValue(newVal) {
      if (!newVal || (!newVal.id && newVal.name == null && !newVal.kind)) {
        this.value = this.generateEmpty()
      }
      this.hydrateAttributes()
      this.hydrateTicketMeta()
    },
    categories() {
      this.hydrateAttributes()
    },
    'value.category_id'() {
      this.onCategoryChange()
    },
  },
}
</script>
